import React, { useState, useEffect, useRef } from 'react';
import {
  LogOut, Search, Download, Eye, CheckCircle, X,
  AlertCircle, Clock, CheckSquare, Trash2, ChevronDown, ChevronUp, Package
} from 'lucide-react';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [error, setError] = useState('');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  useEffect(() => { loadOrders(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const base = process.env.REACT_APP_API_BASE;
      const res = await fetch(`${base}/api/admin/orders`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.orders)) {
        setOrders(data.orders);
      } else {
        setError('Failed to load orders (invalid response).');
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOrder = async (orderId) => {
    setVerifying(orderId);
    try {
      const base = process.env.REACT_APP_API_BASE;
      const res = await fetch(`${base}/api/admin/orders/${orderId}/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setOrders((prev) =>
          prev.map((order) =>
            order._id === orderId
              ? { ...order, status: 'verified', payment_status: 'verified', verifiedAt: new Date().toISOString() }
              : order
          )
        );
      }
    } catch (err) {
      console.error('Error verifying order:', err);
      alert('Failed to verify order. Please try again.');
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) return;
    setDeleting(orderId);
    try {
      const base = process.env.REACT_APP_API_BASE;
      const res = await fetch(`${base}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to delete order');
      const data = await res.json();
      if (data.success) {
        setOrders((prev) => prev.filter((order) => order._id !== orderId));
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const getEmail = (order) =>
    order.customer?.email || order.email || order.user?.email || '';

  const filteredOrders = orders.filter((order) => {
    if (filter === 'pending' && order.status !== 'pending_verification' && order.payment_status !== 'pending_verification') return false;
    if (filter === 'verified' && order.status !== 'verified' && order.payment_status !== 'verified') return false;
    if (filter === 'no-proof' && order.paymentProofSubmitted !== false) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        (order.orderNumber || '').toLowerCase().includes(search) ||
        getEmail(order).toLowerCase().includes(search) ||
        `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.toLowerCase().includes(search) ||
        (order.ship_to_name || '').toLowerCase().includes(search)
      );
    }
    return true;
  });

  const exportFilteredEmails = () => {
    if (filteredOrders.length === 0) { alert('No orders to export with current filter'); return; }
    const emails = filteredOrders.map((o) => getEmail(o)).filter(Boolean).join('\n');
    const blob = new Blob([emails], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emails-${filter}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.URL.revokeObjectURL(url);
  };

  const exportFilteredOrders = () => {
    if (filteredOrders.length === 0) { alert('No orders to export with current filter'); return; }

    const headers = [
      'Order Number', 'Order Date', 'Status',
      'First Name', 'Last Name', 'Email', 'Phone', 'Telegram',
      'Subtotal', 'Discount', 'Total Amount',
      'Payment Method', 'Coupon Code', 'Order Notes', 'Items Count', 'Items Details',
    ];

    const rows = filteredOrders.map((order) => {
      const items = order.items || order.cart || order.cartItems || order.line_items || [];
      const itemsDetails = items.length > 0
        ? items.map((item) => `${item.name || item.title} (${item.size || 'N/A'}) x${item.quantity} - $${Number(item.price).toFixed(2)}`).join('; ')
        : 'No items';
      return [
        order.orderNumber || '',
        new Date(order.created_at || order.createdAt).toLocaleString(),
        order.status || order.payment_status || '',
        order.customer?.firstName || '',
        order.customer?.lastName || '',
        getEmail(order),
        order.customer?.phone || '',
        order.customer?.telegram || '',
        Number(order.subtotal || 0).toFixed(2),
        Number(order.discount || 0).toFixed(2),
        Number(order.total || 0).toFixed(2),
        order.payment_method || order.paymentMethod || '',
        order.coupon_code || 'None',
        order.order_notes || order.orderNotes || '',
        items.length,
        itemsDetails,
      ];
    });

    const csv = [headers.join(','), ...rows.map((row) => row.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${filter}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.URL.revokeObjectURL(url);
  };

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === 'pending_verification' || o.payment_status === 'pending_verification').length,
    verified: orders.filter((o) => o.status === 'verified' || o.payment_status === 'verified').length,
    noProof: orders.filter((o) => o.paymentProofSubmitted === false).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Order Management</h1>
              <p className="text-sm text-gray-600">AFTH Admin Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={loadOrders} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Clock className="w-4 h-4" /> Refresh
              </button>
              <button onClick={() => window.location.assign('/')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <LogOut className="w-4 h-4" /> Back to site
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Orders', value: stats.total, color: 'blue', Icon: CheckSquare },
            { label: 'Pending Verification', value: stats.pending, color: 'orange', Icon: Clock },
            { label: 'Verified', value: stats.verified, color: 'green', Icon: CheckCircle },
            { label: 'No Payment Proof', value: stats.noProof, color: 'red', Icon: AlertCircle },
          ].map(({ label, value, color, Icon }) => (
            <div key={label} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{label}</p>
                  <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
                </div>
                <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${color}-500`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters + search + export */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order #, name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
               <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({stats.total})
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'pending' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({stats.pending})
              </button>
              <button
                onClick={() => setFilter('verified')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'verified' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Verified ({stats.verified})
              </button>
              <button
                onClick={() => setFilter('no-proof')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'no-proof' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                No Proof ({stats.noProof})
              </button>
            </div>

            {/* Export dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export ({filteredOrders.length})
                <ChevronDown className={`w-4 h-4 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {exportDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[250px]">
                  <button
                    onClick={() => { exportFilteredEmails(); setExportDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    📧 Export Emails Only (.txt)
                  </button>
                  <button
                    onClick={() => { exportFilteredOrders(); setExportDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    📊 Export Full Details (.csv)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Orders table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Order #</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Items</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Payment Proof</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">No orders found</td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <React.Fragment key={order._id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {expandedOrder === order._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <div>
                              <span className="font-medium text-gray-900">#{order.orderNumber}</span>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(order.created_at || order.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {order.customer?.firstName} {order.customer?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{getEmail(order)}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <div>📞 {order.customer?.phone}</div>
                          <div>💬 {order.customer?.telegram}</div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="font-semibold text-gray-900">${Number(order.total).toFixed(2)}</div>
                          {order.subtotal != null && (
                            <div className="text-xs text-gray-500">Subtotal: ${Number(order.subtotal).toFixed(2)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                            <Package className="w-3 h-3" />
                            {(order.items || []).length} items
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {order.status === 'verified' || order.payment_status === 'verified' ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              <CheckCircle className="w-3 h-3" /> Verified
                            </span>
                          ) : order.status === 'pending_verification' || order.payment_status === 'pending_verification' ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              <AlertCircle className="w-3 h-3" /> No Proof
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {order.paymentProofUrl ? (
                            <button
                              onClick={() => setSelectedImage(order.paymentProofUrl)}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded hover:bg-blue-200 transition-colors"
                            >
                              <Eye className="w-4 h-4" /> View
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">No proof</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {order.status !== 'verified' && order.payment_status !== 'verified' && order.paymentProofUrl ? (
                              <button
                                onClick={() => handleVerifyOrder(order._id)}
                                disabled={verifying === order._id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                {verifying === order._id ? (
                                  <>
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifying...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-3 h-3" /> Verify
                                  </>
                                )}
                              </button>
                            ) : (order.status === 'verified' || order.payment_status === 'verified') ? (
                              <span className="text-green-600 text-xs font-medium">✓ Verified</span>
                            ) : null}
                            <button
                              onClick={() => handleDeleteOrder(order._id, order.orderNumber)}
                              disabled={deleting === order._id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {deleting === order._id ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-3 h-3" /> Delete
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedOrder === order._id && (
                        <tr>
                          <td colSpan="8" className="px-6 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-3">🗒️ Order Notes</h3>
                                <div className="text-sm">
                                  {order.order_notes || order.orderNotes ? (
                                    <p className="text-gray-700 whitespace-pre-wrap">{order.order_notes || order.orderNotes}</p>
                                  ) : (
                                    <p className="text-gray-400 italic">No notes provided</p>
                                  )}
                                </div>
                                <div className="mt-4 pt-3 border-t border-gray-100 space-y-1 text-sm">
                                  <div><span className="font-medium">Payment:</span> {order.payment_method || order.paymentMethod}</div>
                                  {order.coupon_code && <div><span className="font-medium">Coupon:</span> {order.coupon_code}</div>}
                                </div>
                              </div>

                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-3">🛍️ Order Items</h3>
                                {(() => {
                                  const items = order.items || [];
                                  if (items.length > 0) {
                                    return (
                                      <div className="space-y-3">
                                        {items.map((item, idx) => (
                                          <div key={idx} className="flex items-start gap-3 p-2 bg-gray-50 rounded border border-gray-100">
                                            {item.image && (
                                              <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                            )}
                                            <div className="flex-1 text-sm">
                                              <div className="font-medium text-gray-900">{item.name}</div>
                                              {item.size && <div className="text-gray-600">Size: {item.size}</div>}
                                              <div className="text-gray-600">Qty: {item.quantity} × ${item.price.toFixed(2)}</div>
                                              {item.itemType && <div className="text-gray-500 text-xs capitalize">{item.itemType}</div>}
                                            </div>
                                            <div className="font-semibold text-gray-900">
                                              ${(item.price * item.quantity).toFixed(2)}
                                            </div>
                                          </div>
                                        ))}
                                        <div className="pt-3 border-t border-gray-200 space-y-1 text-sm">
                                          <div className="flex justify-between">
                                            <span>Subtotal:</span>
                                            <span>${Number(order.subtotal || 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200">
                                            <span>Total:</span>
                                            <span>${Number(order.total).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div>
                                      <p className="text-red-500 text-sm mb-2 font-medium">⚠️ No items found — raw order keys:</p>
                                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-48 text-gray-700">
                                        {JSON.stringify(order, null, 2)}
                                      </pre>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          Showing {filteredOrders.length} of {orders.length} orders
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button onClick={() => setSelectedImage(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors">
              <X className="w-8 h-8" />
            </button>
            <img src={selectedImage} alt="Payment proof" className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}